import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";
import { WorkoutExerciseType } from "../models/workoutPlanViewModel";
import { EXERCISE_TYPE_OPTIONS } from "./ExerciseTypePicker";

export interface ICreateExerciseOptions {
  exerciseType: WorkoutExerciseType;
  isResistanceBand: boolean;
}

interface ICreateExerciseSheetProps {
  /** The name typed into the picker; null keeps the sheet closed. */
  name: string | null;
  onClose: () => void;
  onCreate: (name: string, options: ICreateExerciseOptions) => void;
}

/**
 * The last step of "+ Създай ново упражнение": how the new exercise is measured and
 * whether it uses a resistance band. Both become the exercise's own settings; the type
 * can still be changed per workout on the card.
 */
export const CreateExerciseSheet = ({ name, onClose, onCreate }: ICreateExerciseSheetProps) => {
  const isOpen = name !== null;
  const [exerciseType, setExerciseType] = useState<WorkoutExerciseType>(WorkoutExerciseType.KgReps);
  const [isResistanceBand, setIsResistanceBand] = useState(false);

  // Every new exercise starts from the defaults. Keyed on the name only: the dialog
  // re-renders (a running rest timer ticks every second) with a fresh onClose, and
  // that must not wipe a choice the user is in the middle of making.
  useEffect(() => {
    if (name === null) return;
    setExerciseType(WorkoutExerciseType.KgReps);
    setIsResistanceBand(false);
  }, [name]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.rest_picker_backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={styles.rest_picker_sheet}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-label="Ново упражнение"
          >
            <div className={styles.rest_picker_title}>Ново упражнение</div>
            <div className={styles.create_ex_name}>{name}</div>

            <div className={styles.create_ex_section_label}>Тип</div>
            <div className={styles.type_picker_list} role="radiogroup" aria-label="Тип упражнение">
              {EXERCISE_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  role="radio"
                  aria-checked={option.type === exerciseType}
                  className={classNames(styles.type_picker_item, {
                    [styles.type_picker_item_active]: option.type === exerciseType,
                  })}
                  onClick={() => setExerciseType(option.type)}
                >
                  <div className={styles.type_picker_item_label}>{option.label}</div>
                  <div className={styles.type_picker_item_sub}>{option.sub}</div>
                </button>
              ))}
            </div>

            <label className={styles.rest_picker_toggle}>
              <input
                type="checkbox"
                checked={isResistanceBand}
                onChange={(event) => setIsResistanceBand(event.target.checked)}
              />
              <span className={styles.rest_picker_toggle_box} aria-hidden>
                {isResistanceBand ? "✓" : ""}
              </span>
              <span className={styles.rest_picker_toggle_text}>
                <span className={styles.rest_picker_toggle_label}>С ластик</span>
                <span className={styles.rest_picker_toggle_sub}>
                  Всеки сет записва и съпротивлението на ластика в кг.
                </span>
              </span>
            </label>

            <div className={styles.create_ex_actions}>
              <button type="button" className={styles.create_ex_cancel} onClick={onClose}>
                Отказ
              </button>
              <button
                type="button"
                className={styles.create_ex_ok}
                onClick={() => {
                  if (name) onCreate(name, { exerciseType, isResistanceBand });
                  onClose();
                }}
              >
                Създай
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
